import * as React from 'react';
import * as Metadata from '../../../entities/Metadata';
import AccordionSection, { AccordionSectionRef } from "../AccordionSection";
import { IContextualMenuItem, Icon, IconButton, IButton, IRefObject, DirectionalHint, TooltipDelay, TooltipHost, Link } from 'office-ui-fabric-react';
import createLazyContainer from './../../../lazyContainer';
import { IControlConfiguration, ISectionUIControlProps, UIControlHeaderProps } from "../interfaces/ISectionUIControlProps";
import { Dictionary, EntityType, IWarning, UserPreferencesSettingsUpdate } from "../../../entities/common";
import SectionReports, { ISectionReports } from '../../reporting/SectionReports';
import { UserState } from '../../../store/User';
import { CommonOperations, contains } from '../../../store/permissions';
import { notEmpty } from '../../utils/common';
import InaccessibleSection from './InaccessibleSection';
import Logo, { ILogoProps } from '../Logo';
import { ExtensionInfo } from '../../../store/ExtensionStore';
import ExtensionPanel from '../ExtensionPanel';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import WorkWeekTooltip from './uiControls/myWorkWeekControl/WorkWeekTooltip';

export interface ISectionHeaderSettings {
    logo?: ILogoProps;
    label?: string;
}

interface ISectionControlProps {
    section: ISectionInfo;
    index: number;
    entity: { id: string, isEditable?: boolean; canConfigure?: boolean; canCollaborate?: boolean; };
    entityType?: EntityType,
    onChange: (index: number, changes: Partial<ISectionInfo>) => void;
    onHeaderMiddleRender?: () => JSX.Element | JSX.Element[] | null;
    onSaveSettings?: (sectionId: string, update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => void;
    warnings?: IWarning[];
    reporting?: ISectionReports;
    isViewSelected?: boolean;
    controlsConfig?: Dictionary<IControlConfiguration>;
    sectionSettings?: Dictionary<unknown>;
    hideActions?: boolean;

    onToggleFullScreen: (value: boolean) => void;
    isFullScreen: boolean;

    scrollToSection: (sectionId: string) => void;
};

export interface ISectionInfo extends Metadata.Section {
    isConfigureMode: boolean;
    isOpen: boolean;
    isAccessible: boolean;
    headerSettings: ISectionHeaderSettings;
}

const configurableControls = new Set(['FieldsArea', 'StatusesControl', 'KPIControl', 'ScheduleControl', 'EmbeddedContentControl', 'LinkedRoadmapControl']);

type SectionElement = {
    key: string;
    header?: JSX.Element;
    menuItems?: IContextualMenuItem[];
    hideReports?: boolean;
    body: JSX.Element;
    helpUrl?: string;
    extensions?: ExtensionInfo[];
}

const controls: Dictionary<any> = {};

const SectionControl = React.forwardRef<AccordionSectionRef, ISectionControlProps>((props, ref) => {
    const { section, controlsConfig, sectionSettings, entity, warnings, isViewSelected, isFullScreen, onToggleFullScreen, entityType } = props;
    const [isExtensionPanelOpen, setIsExtensionPanelOpen] = React.useState<ExtensionInfo | undefined>(undefined);
    const [locked, setLocked] = React.useState<boolean | undefined>(section.isLockable ? true : undefined);

    const onSectionChange = (changes: Partial<ISectionInfo>) => {
        props.onChange(props.index, changes);
    }

    const toggleFullScreen = () => {
        onToggleFullScreen(!isFullScreen);
        onSectionChange({ isOpen: true });
    }

    const fullScreenBtn = React.createRef<IButton>();
    React.useLayoutEffect(() => {
        if (isFullScreen) {
            fullScreenBtn.current?.focus();
        }
    }, [isFullScreen]);

    React.useEffect(() => () => {
        if (isFullScreen) {
            toggleFullScreen();
        }
    }, []);

    const onSaveSettings = (uiControlId: string, update: UserPreferencesSettingsUpdate, sendToServer?: boolean) => {
        const updateWithUiControlId: UserPreferencesSettingsUpdate = {
            ...update,
            parentSettingsPathKeys: [uiControlId, ...update.parentSettingsPathKeys]
        };
        props.onSaveSettings?.(section.id, updateWithUiControlId, sendToServer);
    }

    const commonControlsProps = {
        entity: entity,
        entityType: entityType,
        warnings: warnings,
        isConfigureMode: section.isConfigureMode,
        isViewSelected: isViewSelected,
        sectionId: section.id,
        sectionLabel: Metadata.getLabel(section),
        isFullScreen: isFullScreen
    };

    const elements: SectionElement[] = section.uiControls.map((uiControl, index) => {
        const config = controlsConfig?.[uiControl.type] ?? {};
        const settings = (config.settingsBuilder
            ? config.settingsBuilder?.((sectionSettings?.[uiControl.id]) as any ?? Empty)
            : sectionSettings?.[uiControl.id]) ?? Empty;

        let control = controls[uiControl.type];
        if (!control) {
            control = controls[uiControl.type] = createLazyContainer<ISectionUIControlProps>(() => import(`./uiControls/${uiControl.type}.tsx`));
        }

        const controlProps: ISectionUIControlProps = {
            ...commonControlsProps,
            id: uiControl.id,
            key: uiControl.id,
            actions: config.actions ?? Empty,
            editProps: {
                uiControlElementsCustomRender: config.editProps?.elementCustomRender,
                customFieldValidatorBuilder: config.editProps?.customFieldValidator,
                deriveDisplayAttributes: config.editProps?.deriveDisplayAttributes,
                buildSavePayload: config.editProps?.buildSavePayload,
            },
            inlineEditProps: {
                uiControlElementsCustomRender: config.inlineEditProps?.elementCustomRender,
                customFieldValidatorBuilder: config.inlineEditProps?.customFieldValidator,
            },
            datacontext: config.datacontext,
            controlSettings: settings,
            settings: uiControl.settings,
            switchToConfigureMode: () => onSectionChange({ isConfigureMode: true }),
            onConfigureModeComplete: () => onSectionChange({ isConfigureMode: false }),
            onSaveSettings: props.onSaveSettings
                ? (update, sendToServer) => onSaveSettings(uiControl.id, update, sendToServer)
                : undefined,
            scrollToSection: props.scrollToSection,
            disabled: locked
        }

        const headerProps: UIControlHeaderProps<unknown> = {
            settings: settings,
            isLoaded: !!props.onSaveSettings,
            onChange: ({ expand, update }) => {
                onSectionChange({ isOpen: expand });
                onSaveSettings(uiControl.id, { parentSettingsPathKeys: [], valueBySettingNameMap: update });
            }
        }

        return {
            key: uiControl.id,
            header: config.headerRender?.(headerProps),
            menuItems: config.buildMenuItems?.(headerProps, controlProps, section),
            hideReports: config.hideReports,
            body: React.createElement(control, controlProps),
            helpUrl: config.helpUrl,
            extensions: config.extensions
        }
    });

    const onKeyDown = (ev: React.KeyboardEvent<any>) => {
        if (isFullScreen && ev.key === "Escape") {
            toggleFullScreen();
        }
    }

    const onSectionStateChange = () => {
        if (!isFullScreen) {
            onSectionChange({ isOpen: !section.isOpen });
        }
    }

    return <>
        <AccordionSection
            isOpen={section.isOpen || isFullScreen}
            className={`section ${section.settings.className || ''} ${section.isConfigureMode ? "configure-mode" : ""} ${isFullScreen ? 'maximized' : ""}`}
            ref={ref}
            onExpand={() => !isFullScreen && onSectionChange({ isOpen: true })}
            onCollapse={() => !isFullScreen && onSectionChange({ isOpen: false })}
            onRenderHeader={() => <SectionHeader
                {...props}
                ref={fullScreenBtn}
                sectionElements={elements}
                isFullScreen={isFullScreen}
                toggleFullScreen={toggleFullScreen}
                onChange={onSectionChange}
                onClick={onSectionStateChange}
                onExtensionClick={setIsExtensionPanelOpen}
                locked={locked}
                onUnlock={section.isLockable ? () => setLocked(false) : undefined}
            />}
            onRenderBody={() => section.isAccessible
                ? <>{elements.map(_ => _.body)}</>
                : <InaccessibleSection sectionName={section.name} />
            }
            onKeyDown={onKeyDown}
        />
        {isExtensionPanelOpen && <ExtensionPanel
            entityType={entityType!}
            info={isExtensionPanelOpen}
            onDismiss={() => setIsExtensionPanelOpen(undefined)}
            context={{ entityType: entityType! }}
        />}
    </>;
});

export default SectionControl;

const Empty = {}

type SectionHeaderProps = {
    section: ISectionInfo;
    entity: { id: string, isEditable?: boolean; canConfigure?: boolean; canCollaborate?: boolean; };
    onChange: (changes: Partial<ISectionInfo>) => void;
    onHeaderMiddleRender?: () => JSX.Element | JSX.Element[] | null;
    reporting?: ISectionReports;
    isViewSelected?: boolean;
    isFullScreen: boolean;
    hideActions?: boolean;
    toggleFullScreen: () => void;

    sectionElements: SectionElement[];
    onClick: () => void;
    onExtensionClick: (ext: ExtensionInfo) => void;

    locked?: boolean;
    onUnlock?: () => void;
}

type SectionHeaderOwnProps = {
    user: UserState;
    fullscreenBtn: IRefObject<IButton>;
}

const RESOURCE_WORK_WEEK_SECTION_ID = 'db5d0ef6-0bbd-4b42-b885-03c91e4f63f2';

const sectionTooltips: Dictionary<JSX.Element> = {
    [RESOURCE_WORK_WEEK_SECTION_ID]: <WorkWeekTooltip />,
};

const SectionHeaderControl = connect((state: ApplicationState) => ({ user: state.user }))((props: SectionHeaderProps & SectionHeaderOwnProps) => {
    const { section, user, sectionElements, isViewSelected, entity, onChange, onHeaderMiddleRender, reporting, isFullScreen, hideActions, toggleFullScreen, onClick, onExtensionClick } = props;
    const isConfigurable = section.uiControls.length === 1 && configurableControls.has(section.uiControls[0].type) && !hideActions;
    const { headerSettings } = section;
    const tooltip = section.settings.tooltip ?? sectionTooltips[section.id];
    const items = !section.isConfigureMode && isConfigurable
        ? [{
            key: 'configure',
            iconProps: { iconName: 'Settings' },
            name: 'Configure',
            disabled: !contains(user.permissions.common, CommonOperations.ConfigurationView)
                && (isViewSelected || !entity.canConfigure) || !section.isAccessible,
            title: entity.canConfigure && isViewSelected && !contains(user.permissions.common, CommonOperations.ConfigurationManage)
                ? 'Please select default View to enable configuration.'
                : '',
            onClick: () => onChange({ isOpen: true, isConfigureMode: true })
        }]
        : [];

    return <div className="align-center">
        <div className="title align-center overflow-text" onClick={onClick} aria-hidden >
            <span className="chevron"></span>
            {isFullScreen && headerSettings &&
                <>
                    <Logo {...headerSettings.logo} />
                    <span className="overflow-text">{headerSettings.label}</span>
                    <span className="breadcrumbs-separator">/</span>
                </>
            }
            {section.settings.iconName && <Icon className="section-title-icon" iconName={section.settings.iconName} />}
            <span>{Metadata.getLabel(section)}</span>
            {tooltip && <TooltipHost content={tooltip} delay={TooltipDelay.zero} hostClassName='tooltip' className='text-hint' calloutProps={{ directionalHint: DirectionalHint.rightCenter }}>
                <Icon iconName="Info" />
            </TooltipHost>}
            {props.locked === undefined
                ? undefined
                : <Icon iconName={props.locked ? "lock" : "unlock"} title={props.locked ? 'Locked' : 'Unlocked'} />
            }
        </div>
        {onHeaderMiddleRender?.()}
        <div className="menu align-center" onClick={(e) => e.stopPropagation()}>
            {
                props.locked === true && props.onUnlock
                    ? <Link onClick={() => { props.onUnlock?.(); }} >Edit Section</Link>
                    : <></>
            }
            {
                sectionElements.filter(_ => _.extensions?.length).flatMap(_ => _.extensions!).map(ext =>
                    <IconButton
                        key={ext.id}
                        iconProps={{ iconName: 'Puzzle' }}
                        title={ext.name}
                        onClick={() => onExtensionClick(ext)}
                    />)
            }
            {
                sectionElements.filter(_ => _.header).map(_ =>
                    <React.Fragment key={_.key}>{_.header}</React.Fragment>)
            }
            {
                !hideActions && <SectionReports
                    reporting={sectionElements.some(_ => _.hideReports) ? undefined : reporting}
                    items={items}
                    entity={entity}
                    sectionName={section.name}
                    getMenuItems={() => sectionElements.map(_ => _.menuItems).filter(notEmpty).flat()}
                />
            }
            {
                sectionElements.filter(_ => _.helpUrl).map(_ =>
                    <IconButton
                        key='help'
                        iconProps={{ iconName: "PPMXQuestionMarkInCircle" }}
                        title={`${section.name} help`}
                        href={_.helpUrl}
                        target="_blank" />)
            }
            <IconButton
                key='fullscreen'
                componentRef={props.fullscreenBtn}
                title={`${isFullScreen ? "Exit" : "Enter"} full screen mode`}
                iconProps={{ iconName: isFullScreen ? "BackToWindow" : "Fullscreen" }}
                onClick={toggleFullScreen} />
        </div>
    </div>
});

const SectionHeader = React.forwardRef((props: SectionHeaderProps, ref: IRefObject<IButton>) => 
    <SectionHeaderControl {...props} fullscreenBtn={ref} />
);